import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, Pagination, Typography } from '@mui/material';
import { SitePlanItem } from '../types';
import SitePlanCard from './SitePlanCard';

const PAGE_SIZE = 20;

interface SitePlanSectionProps {
  plans: SitePlanItem[];
  onPlanClick?: (plan: SitePlanItem) => void;
}

export const SitePlanSection: React.FC<SitePlanSectionProps> = ({ plans, onPlanClick }) => {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(plans.length / PAGE_SIZE));

  // Reset to page 1 when the list shrinks (e.g. search filter changes).
  useEffect(() => {
    if (page > pageCount) setPage(1);
  }, [page, pageCount]);

  const visiblePlans = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return plans.slice(start, start + PAGE_SIZE);
  }, [plans, page]);

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          mb: 2,
          color: 'var(--color-text-strong)',
          fontSize: { xs: '1rem', sm: '1.25rem' },
        }}
      >
        Site plan
      </Typography>
      {plans.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
          No site plans match your search.
        </Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            {visiblePlans.map((p) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>
                <SitePlanCard plan={p} onClick={onPlanClick} />
              </Grid>
            ))}
          </Grid>
          {pageCount > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
                shape="rounded"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default SitePlanSection;
